import * as React from "react";
import { CheckCircle2, Loader2, Save, AlertCircle } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@eduviaos/ui-kit";
import { ApiError } from "@eduviaos/shared";
import { AssetUploader } from "@/components/branding/AssetUploader";
import { BrandColourPicker } from "@/components/branding/BrandColourPicker";
import { useTenantBranding, useCommitBranding } from "@/lib/branding/api";

const LOGO_TYPES = ["image/png", "image/jpeg", "image/svg+xml", "image/webp"] as const;
const FAVICON_TYPES = [
  "image/png",
  "image/svg+xml",
  "image/x-icon",
  "image/vnd.microsoft.icon",
] as const;

const LOGO_MAX = 5 * 1024 * 1024;
const FAVICON_MAX = 256 * 1024;

const FAVICON_FALLBACK_URL = (slug: string) =>
  `${(import.meta.env.VITE_API_URL as string) ?? ""}/tenants/${slug}/branding/favicon.svg`;

function formatDate(iso: string | null) {
  if (!iso) return "never";
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

interface BrandingPanelProps {
  /** Tenant slug — used purely for the read query + favicon fallback
   * preview. All WRITES are keyed on the authed admin's JWT, never this. */
  slug: string;
  /** When true, hides the floating "Unsaved" footer and the intro row —
   * used inside the first-run wizard where the step footer owns the CTA. */
  embedded?: boolean;
  /** Fired after a successful save so a host (first-run) can advance. */
  onSaved?: () => void;
}

/**
 * Tenant branding editor (Phase F2b — IA self-serve).
 *
 * Ported from the super-admin `BrandingPanel` but wired to the
 * tenant-scoped `/me/branding` surface: the institution admin refines
 * its OWN logo, favicon and primary/secondary colours during onboarding
 * (and later from Settings). Saving primes the `["branding", slug]`
 * cache that `<TenantBrandingProvider>` reads, so the IA shell repaints
 * to the new palette without a reload — and the same branding is served
 * to the student portal via the public branding API.
 */
export function BrandingPanel({ slug, embedded = false, onSaved }: BrandingPanelProps) {
  const branding = useTenantBranding(slug);
  const commit = useCommitBranding();

  const [stagedLogoUrl, setStagedLogoUrl] = React.useState<string | null>(null);
  const [stagedFaviconUrl, setStagedFaviconUrl] = React.useState<string | null>(null);
  const [primary, setPrimary] = React.useState<string | null>(null);
  const [secondary, setSecondary] = React.useState<string | null>(null);
  const [savedFlash, setSavedFlash] = React.useState(false);

  React.useEffect(() => {
    if (branding.data) {
      setPrimary(branding.data.primary_color);
      setSecondary(branding.data.secondary_color);
    }
  }, [branding.data]);

  const onSave = async () => {
    try {
      await commit.mutateAsync({
        logo_url: stagedLogoUrl ?? undefined,
        favicon_url: stagedFaviconUrl ?? undefined,
        primary_color: primary,
        secondary_color: secondary,
      });
      setStagedLogoUrl(null);
      setStagedFaviconUrl(null);
      setSavedFlash(true);
      window.setTimeout(() => setSavedFlash(false), 1400);
      onSaved?.();
    } catch {
      // Surfaced below from commit.error
    }
  };

  if (branding.isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="text-muted-foreground size-5 animate-spin" aria-hidden="true" />
      </div>
    );
  }

  if (branding.isError || !branding.data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Branding unavailable</CardTitle>
          <CardDescription>
            {(branding.error as Error)?.message ?? "Could not load branding for your institution."}
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const data = branding.data;
  const dirty =
    Boolean(stagedLogoUrl) ||
    Boolean(stagedFaviconUrl) ||
    primary !== data.primary_color ||
    secondary !== data.secondary_color;

  const commitError =
    commit.error instanceof ApiError ? commit.error.message : commit.error?.message;

  return (
    <div className="grid gap-4">
      {!embedded && (
        <div className="flex flex-wrap items-end justify-between gap-3">
          <p className="text-muted-foreground text-sm">
            Logo, favicon, and brand colours used across your admin portal and your learners&apos;
            portal. Last edited {formatDate(data.updated_at)}.
          </p>
          <Button onClick={onSave} disabled={!dirty || commit.isPending} size="sm">
            {commit.isPending ? (
              <>
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                Saving…
              </>
            ) : savedFlash ? (
              <>
                <CheckCircle2 className="size-4" aria-hidden="true" />
                Saved
              </>
            ) : (
              <>
                <Save className="size-4" aria-hidden="true" />
                Save changes
              </>
            )}
          </Button>
        </div>
      )}

      {commitError && (
        <div
          role="alert"
          className="border-destructive/40 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border px-3 py-2.5 text-sm"
        >
          <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <span>{commitError}</span>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <AssetUploader
          kind="logo"
          title="Logo"
          description="Shown in your admin sidebar and your learners' portal. Transparent PNG / SVG recommended for dark mode parity."
          acceptMimeTypes={LOGO_TYPES}
          acceptHelp="PNG, JPG, SVG, WebP"
          maxBytes={LOGO_MAX}
          currentUrl={stagedLogoUrl ?? data.logo_url}
          onUploaded={(url) => setStagedLogoUrl(url)}
          onDeleted={() => setStagedLogoUrl(null)}
        />
        <AssetUploader
          kind="favicon"
          title="Favicon"
          description="Browser tab icon. When unset, we serve a deterministic first-letter icon from your institution name."
          acceptMimeTypes={FAVICON_TYPES}
          acceptHelp="PNG, SVG, ICO"
          maxBytes={FAVICON_MAX}
          currentUrl={stagedFaviconUrl ?? data.favicon_url}
          onUploaded={(url) => setStagedFaviconUrl(url)}
          onDeleted={() => setStagedFaviconUrl(null)}
          fallbackPreview={
            <img
              src={FAVICON_FALLBACK_URL(slug)}
              alt="Auto-generated favicon"
              className="h-full w-full object-contain"
            />
          }
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Brand colours</CardTitle>
          <CardDescription>
            Applied as CSS variables across your admin portal and your learners&apos; portal. Leave
            empty to fall back to the platform default.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-6 md:grid-cols-2">
          <BrandColourPicker
            id="primary-color"
            label="Primary"
            description="Drives the main accent (active nav, primary buttons, focus rings)."
            value={primary}
            onChange={setPrimary}
          />
          <BrandColourPicker
            id="secondary-color"
            label="Secondary"
            description="Used for secondary buttons and complementary highlights."
            value={secondary}
            onChange={setSecondary}
            defaultColor="#10b981"
          />
        </CardContent>
      </Card>

      {embedded && (
        <div className="flex items-center justify-end gap-3">
          {savedFlash && (
            <span className="flex items-center gap-1.5 text-sm text-emerald-700 dark:text-emerald-400">
              <CheckCircle2 className="size-4" aria-hidden="true" />
              Saved
            </span>
          )}
          <Button onClick={onSave} disabled={commit.isPending} size="sm">
            {commit.isPending ? (
              <>
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                Saving…
              </>
            ) : (
              <>
                <Save className="size-4" aria-hidden="true" />
                Save branding
              </>
            )}
          </Button>
        </div>
      )}

      {!embedded && dirty && (
        <div className="sticky bottom-4 left-0 right-0 z-10">
          <div className="bg-card mx-auto flex max-w-2xl items-center justify-between rounded-md border px-4 py-2.5 shadow-lg">
            <p className="text-sm">
              <Badge variant="warn" className="mr-2 text-[10px]">
                Unsaved
              </Badge>
              Click <strong>Save changes</strong> to publish.
            </p>
            <Button onClick={onSave} disabled={commit.isPending} size="sm">
              {commit.isPending ? (
                <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
              ) : (
                <Save className="size-3.5" aria-hidden="true" />
              )}
              Save
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
